import React from 'react';
import { Alert, Content, Form, FormGroup, Radio, Title } from '@patternfly/react-core';
import type { GpuServerOfferLocation, GpuServerOfferProduct } from '../../../../types/models/GpuServer';
import { countryName, findLocation } from './gpuServerLocation';

interface LocationStepProps {
  /** The product chosen on step 1 (`null` if none yet). */
  product: GpuServerOfferProduct | null;
  locations: GpuServerOfferLocation[];
  selectedLocation: string | null;
  onSelectLocation: (code: string) => void;
  /** A start refusal that belongs to this step (`data.location_code`), shown verbatim. */
  error: string | null;
}

/**
 * Step 2 — choose where the server runs, among the locations with capacity for the chosen product
 * right now. Capacity moves minute to minute, so a refusal here suggests another location.
 */
const LocationStep: React.FunctionComponent<LocationStepProps> = ({
  product,
  locations,
  selectedLocation,
  onSelectLocation,
  error,
}) => (
  <>
    <Title headingLevel="h2" size="lg" className="pf-v6-u-mb-sm">
      Choose a location
    </Title>
    {error !== null && (
      <Alert variant="danger" isInline title="This location cannot be used" className="pf-v6-u-mb-md">
        {error} Capacity changes from minute to minute: choose another location, or retry later.
      </Alert>
    )}
    {product === null ? (
      <Alert variant="info" isInline title="Choose a server first" />
    ) : (
      <>
        <Content component="p">Locations with capacity for {product.label} right now.</Content>
        {/* The wizard's own Next is a submit button, so this form must never submit natively. */}
        <Form onSubmit={(event) => event.preventDefault()}>
          <FormGroup role="radiogroup" fieldId="gpu-location" label="Location" isRequired isStack>
            {product.available_locations.map((code) => {
              const location = findLocation(code, locations);
              return (
                <Radio
                  key={code}
                  id={`gpu-location-${code}`}
                  name="gpu-location"
                  label={location?.name ?? code}
                  description={location ? `${code} · ${countryName(location.country_code)}` : code}
                  isChecked={selectedLocation === code}
                  onChange={() => onSelectLocation(code)}
                />
              );
            })}
          </FormGroup>
        </Form>
      </>
    )}
  </>
);

export default LocationStep;
